import { addDays, isBefore, parse } from "date-fns";
import { PenLine } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CustomPopover } from "@/components/ui/popover-custom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useQuoteContext } from "@/contexts/quote-context";
import { formatKms, formatMoneyBr } from "@/utils/format";
import { ptBR } from "date-fns/locale";

export default function QuoteCardResumeMobile() {
  const {
    cotacao,
    quotePrice,
    updateModifierOperante,
    updateModifierBlindado,
    quoteModifiers,
    updateDateEntrega,
    isContractSigned,
  } = useQuoteContext();

  const date = cotacao.cve_data_previsao_recebimento
    ? parse(cotacao.cve_data_previsao_recebimento, "dd/MM/yyyy", new Date())
    : new Date();

  const minDate = addDays(new Date(), 1);

  const isDateDisabled = (date: Date) => isBefore(date, minDate);

  const vehicleOperatingChange = (value: string) => {
    updateModifierOperante(value === "Sim" ? true : false);
  };

  const vehicleBlindedChange = (value: string) => {
    updateModifierBlindado(value === "Sim" ? true : false);
  };

  const hasColeta = Number(cotacao.tro_codigo) > 1;
  const hasEntrega = Number(cotacao.trd_codigo) > 1;

  const logDateChange = (newDate: Date | undefined) => {
    if (newDate) {
      updateDateEntrega(newDate);
    }
  };

  return (
    <Card className="w-full md:rounded-3xl border-0">
      <CardContent className="p-0">
        <div className="py-6 px-4 md:space-y-2 space-y-2 text-xs font-normal text-n-text">
          <div className="flex flex-col md:gap-1.5 gap-3">
            <div className="flex items-start">
              <span className="text-n-text-neutral w-[140px] flex-shrink-0 text-xs font-bold">
                Código do pedido:
              </span>
              <span className="font-semibold text-n-primary text-xs">
                #{cotacao.ctc_codigo}
              </span>
            </div>

            <Separator className="bg-gray-200" />
            <div className="flex items-start">
              <span className="text-n-text-neutral w-[140px] flex-shrink-0 text-xs font-bold">
                Modelo do veículo:
              </span>
              <span className=" text-xs">{cotacao.vmd_descricao}</span>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex items-start">
              <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                <span className="text-n-text-neutral text-xs font-bold">
                  Rota base:
                </span>
                <CustomPopover
                  title="Rota base"
                  description="Define o percurso de origem e destino do transporte, usado como referência para cálculo de frete."
                />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <div className="flex items-center gap-2 mb-1">
                  {cotacao.cve_rota_texto}
                </div>
              </div>
            </div>
            <Separator className="bg-gray-200" />

            <div className="flex flex-col gap-2">
              <h4 className="text-n-text-neutral text-xs font-bold">
                Endereços adicionais:
              </h4>

              <div className="flex items-start">
                <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                  <span className="text-n-text-neutral text-xs font-bold">
                    {hasColeta ? "Coleta:" : "Recebimento:"}
                  </span>

                  {hasColeta ? (
                    <CustomPopover
                      title="Endereço de Coleta"
                      description="O transportador irá até um endereço específico para retirar o veículo. A distância entre esse ponto e a base de origem gera um custo adicional proporcional ao deslocamento."
                    />
                  ) : (
                    <CustomPopover
                      title="Local de Recebimento"
                      description="O veículo será entregue diretamente na filial de origem, sem custo de deslocamento adicional. O remetente é responsável por levar o veículo até a base."
                    />
                  )}
                </div>
                <div className="flex-1 leading-relaxed font-normal">
                  {cotacao.tro_codigo === "2" ? (
                    <>
                      {cotacao.cve_coleta_cep ? (
                        <>
                          {cotacao.cve_coleta_cep},{" "}
                          {cotacao.cve_coleta_endereco},{" "}
                          {cotacao.cve_coleta_numero} <br />
                          {cotacao.cve_coleta_bairro},{" "}
                          {cotacao.mun_descricao_coleta} <br />
                          Distância da base: {formatKms(cotacao.cve_coleta_kms)}
                        </>
                      ) : (
                        <p className="text-n-text-red">
                          Informe o CEP para calcular o endereço de coleta
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      {cotacao.bop_origem_descricao} <br />
                      {cotacao.municipio_base_origem}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                  <span className="text-n-text-neutral text-xs font-bold">
                    {hasEntrega ? "Entrega:" : "Retirada:"}
                  </span>
                  {hasEntrega ? (
                    <CustomPopover
                      title="Endereço de Entrega"
                      description="O transportador irá até um endereço específico para entregar o veículo. A distância entre a base de destino e esse ponto gera um custo adicional proporcional ao deslocamento."
                    />
                  ) : (
                    <CustomPopover
                      title="Local de Retirada"
                      description="O veículo ficará disponível para retirada diretamente na filial de destino, sem custo de deslocamento adicional. O destinatário é responsável por buscar o veículo na base."
                    />
                  )}
                </div>
                <div className="flex-1 leading-relaxed">
                  {cotacao.trd_codigo === "2" ? (
                    <>
                      {cotacao.cve_entrega_cep ? (
                        <>
                          {cotacao.cve_entrega_cep},{" "}
                          {cotacao.cve_entrega_endereco} ,{" "}
                          {cotacao.cve_entrega_numero} <br />
                          {cotacao.cve_entrega_bairro},{" "}
                          {cotacao.mun_descricao_entrega} <br />
                          Distância da base:{" "}
                          {formatKms(cotacao.cve_entrega_kms)}
                        </>
                      ) : (
                        <p className="text-n-text-red">
                          Informe o CEP para calcular o endereço de entrega
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      {cotacao.bop_destino_descricao} <br />
                      {cotacao.municipio_base_destino}
                    </>
                  )}
                </div>
              </div>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex items-center">
              <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                <span className="text-n-text-neutral text-xs font-bold">
                  Veículo operante:
                </span>
                <CustomPopover
                  title="Veículo operante"
                  description="Indique se o veículo está em condições de operação."
                />
              </div>
              <RadioGroup
                value={quoteModifiers.operante ? "Sim" : "Não"}
                onValueChange={vehicleOperatingChange}
                className="flex items-center gap-6"
                disabled={isContractSigned}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Sim" id="sim" />
                  <Label htmlFor="sim" className="text-xs">
                    Sim
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Não" id="nao" />
                  <Label htmlFor="nao" className="text-xs">
                    Não
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex items-center">
              <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                <span className="text-n-text-neutral text-xs font-bold">
                  Veículo blindado:
                </span>
                <CustomPopover
                  title="Veículo blindado"
                  description="Indique se o veículo é blindado."
                />
              </div>
              <RadioGroup
                value={quoteModifiers.blindado ? "Sim" : "Não"}
                onValueChange={vehicleBlindedChange}
                className="flex items-center gap-6"
                disabled={isContractSigned}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Sim" id="sim" />
                  <Label htmlFor="sim" className="text-xs">
                    Sim
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Não" id="nao" />
                  <Label htmlFor="nao" className="text-xs">
                    Não
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex items-center">
              <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                <span className="text-n-text-neutral text-xs font-bold">
                  Data prevista:
                </span>
                <CustomPopover
                  title="Data prevista"
                  description="Data prevista para retirada do veículo."
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-normal">
                  {cotacao.cve_data_previsao_recebimento
                    ? date.toLocaleDateString("pt-BR")
                    : "Selecionar data"}
                </span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-5 w-5 p-0" disabled={isContractSigned}>
                      <PenLine className="h-3 w-3 text-n-green" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={logDateChange}
                      disabled={isDateDisabled}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex items-center">
              <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                <span className="text-n-text-neutral text-xs font-bold">
                  Distância:
                </span>
                <CustomPopover
                  title="Distância"
                  description="Distância total do percurso."
                />
              </div>
              <span className="font-normal">
                {formatKms(cotacao.cve_rota_kms)}
              </span>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex items-center">
              <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                <span className="text-n-text-neutral text-xs font-bold">
                  Prazo de entrega:
                </span>
                <CustomPopover
                  title="Prazo de Entrega"
                  description="Tempo estimado para entrega do veículo no destino."
                />
              </div>
              <span className="font-normal">
                {cotacao.cve_prazo_entrega} dias úteis
              </span>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex items-center">
              <div className="flex items-center gap-1 w-[140px] flex-shrink-0">
                <span className="text-n-text-neutral text-xs font-bold">
                  Subtotal:
                </span>
                <CustomPopover
                  title="Subtotal"
                  description="Total com todos os valores e descontos aplicados."
                />
              </div>
              <span className="font-normal">
                {formatMoneyBr(quotePrice.subtotal)}
              </span>
            </div>

            <Separator className="bg-gray-200" />

            <div className="flex flex-col gap-2">
              {quotePrice.delivery > 0 && (
                <div className="flex items-center">
                  <span className="text-n-text-neutral w-[140px] flex-shrink-0 text-xs font-bold">
                    Valor Entrega:
                  </span>
                  <span className="font-normal">
                    {formatMoneyBr(quotePrice.delivery)}
                  </span>
                </div>
              )}
              {quotePrice.pickup > 0 && (
                <div className="flex items-center">
                  <span className="text-n-text-neutral w-[140px] flex-shrink-0 text-xs font-bold">
                    Valor Coleta:
                  </span>
                  <span className="font-normal">
                    {formatMoneyBr(quotePrice.pickup)}
                  </span>
                </div>
              )}
              {cotacao.cve_tipo_seguro === "2" && (
                <div className="flex items-center">
                  <span className="text-n-text-neutral w-[140px] flex-shrink-0 text-xs font-bold">
                    Seguro Premium:
                  </span>
                  <span className="font-normal">
                    {formatMoneyBr(quotePrice.insurancePremium)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-start items-center bg-n-green-s-light px-6 md:py-2 py-4  md:rounded-b-3xl">
          <span className="font-semibold text-xs w-[140px]">Total:</span>
          <span className="font-bold text-n-primary text-xl">
            {formatMoneyBr(quotePrice.total)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
